import { useState, useEffect, useCallback, useRef } from 'react'
import { useFetcher } from '@remix-run/react'
import {
  formatSuggestionLabel,
  mapBrendaEntityToAddressObject,
  isValidUkPostcode,
  normalizeUkPostcode,
} from '~/utils'

// Types
import type { BrendaAddressEntity, CustomerAddressProps } from '~/types'

export type AddressSuggestion = {
  id: string
  label: string
  entity: BrendaAddressEntity
}

type LoaderData =
  | { ok: true; entities: BrendaAddressEntity[] }
  | { ok: false; error: string }

interface Props {
  address: string
  addressData: CustomerAddressProps | null
  fetcherKey?: string
  setAddressData: (e: CustomerAddressProps | null) => void
}

// Main export
const useAddressLookup = ({ address, addressData, fetcherKey, setAddressData }: Props) => {
  const fetcher = useFetcher<LoaderData>(fetcherKey ? { key: fetcherKey } : undefined)

  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([])
  const [error, setError] = useState<string>('')

  // Prevents repeated calls when nothing changed
  const lastRequestedPostcodeRef = useRef<string>('')

  // Manual loading state so Remix revalidation cycles
  // (triggered by other fetcher submissions) don't show the spinner
  const [loading, setLoading] = useState(false)

  // Handle fetcher results
  useEffect(() => {
    const data = fetcher.data
    if (!data) return

    setLoading(false)

    if (data.ok) {
      setError('')

      const mapped: AddressSuggestion[] = (data.entities ?? []).map(
        (entity, idx) => ({
          id: `${entity.postcode}-${idx}`,
          label: formatSuggestionLabel(entity),
          entity,
        })
      )

      setSuggestions(mapped)
    } else {
      setSuggestions([])
      console.error(data.error)
      setError('No address suggestions found')
    }
  }, [fetcher.data])

  // Debounced loader trigger (only when postcode changes)
  // Debounced loader trigger (only when postcode changes)
  useEffect(() => {
    const raw = address.trim()

    // If they’ve selected an address, or cleared input: reset and allow future searches
    if (!raw || addressData) {
      lastRequestedPostcodeRef.current = ''
      setSuggestions([])
      setError('')
      return
    }

    // Optional: don’t queue while already loading
    if (fetcher.state !== 'idle') return

    // Always debounce first, THEN validate / fetch
    const queryDelay = setTimeout(() => {
      // UX rule: only show an error once they’ve typed “enough”
      // (and don’t hit the endpoint unless valid)
      if (raw.length >= 5 && !isValidUkPostcode(raw)) {
        setSuggestions([])
        setError('Please enter a valid UK postcode')
        return
      }

      // If it’s too short, keep UI calm (no error, no fetch)
      if (raw.length < 5) {
        setSuggestions([])
        setError('')
        return
      }

      // From here, it’s valid and “complete enough” to search
      const postcode = normalizeUkPostcode(raw)

      // Don’t refire for the same postcode (prevents "over and over" on failures)
      if (postcode === lastRequestedPostcodeRef.current) return

      setError('')
      setLoading(true)
      lastRequestedPostcodeRef.current = postcode

      fetcher.load(
        `/api/get-addresses?postcode=${encodeURIComponent(postcode)}`
      )
    }, 1000)

    return () => clearTimeout(queryDelay)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address, addressData, fetcher.state])

  const getAddressData = useCallback(
    async (id: string) => {
      const selected = suggestions.find(s => s.id === id)

      if (!selected) {
        setAddressData(null)
        setError('Address location data not found')
        return
      }

      setError('')
      setAddressData(mapBrendaEntityToAddressObject(selected.entity))
    },
    [setAddressData, suggestions]
  )

  return {
    error,
    loading,
    suggestions,
    setSuggestions,
    getAddressData,
  }
}

export default useAddressLookup
